import { resolveMediaUrl } from "../utils/url";
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../context/UserContext';

export default function Members() {
  const { user } = useContext(UserContext);
  const [entries, setEntries] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/users/public/members');
        if (!cancelled) {
          setEntries(Array.isArray(data?.entries) ? data.entries : []);
          setHiddenCount(Number(data?.hiddenCount || 0));
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || 'Fehler beim Laden der Mitglieder.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const CardContainer = ({ children }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
      {children}
    </div>
  );

  const visibleCardStyle = {
    width: 220,
    textDecoration: 'none',
    color: '#fff',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease'
  };
  const hiddenCardStyle = {
    width: 220,
    color: '#c9d1d9',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
    border: '1px dashed rgba(255,255,255,0.18)',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12
  };

  const cards = (
    (() => {
      // Group entries by first letter (visible) or '#' for hidden/other
      const normalizeLetter = (ch) => {
        if (!ch) return '#';
        const upper = ch.toUpperCase();
        if (['Ä'].includes(upper)) return 'A';
        if (['Ö'].includes(upper)) return 'O';
        if (['Ü'].includes(upper)) return 'U';
        if (!upper.match(/[A-Z]/)) return '#';
        return upper;
      };

      // Role resolution: pick highest non-owner; if only owner then owner.
      const ROLE_ORDER = {
        owner: 6,
        admin: 5,
        moderator: 4,
        mentor: 3,
        member: 1,
        'anwärter': 0
      };
      const ROLE_LABEL = {
        owner: 'Owner',
        admin: 'Admin',
        moderator: 'Moderator',
        mentor: 'Mentor',
        member: 'Mitglied',
        'anwärter': 'Anwärter'
      };
      const resolveDisplayRole = (entry) => {
        const roles = Array.isArray(entry?.roles) ? entry.roles.filter(r => ROLE_ORDER[r] !== undefined) : [];
        if (roles.length === 0) return 'Mitglied';
        const sorted = roles.slice().sort((a,b) => ROLE_ORDER[b] - ROLE_ORDER[a]);
        const top = sorted[0];
        if (top === 'owner') {
          const second = sorted.find(r => r !== 'owner');
          return second ? ROLE_LABEL[second] || second : ROLE_LABEL[top] || top; // fallback to owner if no second
        }
        return ROLE_LABEL[top] || top;
      };
      const groups = new Map();
      entries.forEach((e, idx) => {
        let letter;
        if (e.type === 'visible') {
          const dn = (e.displayName || e.username || '').trim();
          letter = normalizeLetter(dn.charAt(0));
        } else {
          letter = '#';
        }
        if (!groups.has(letter)) groups.set(letter, []);
        groups.get(letter).push({ entry: e, idx });
      });
      // Sort letters A-Z then '#' last
      const letters = Array.from(groups.keys()).sort((a,b) => {
        if (a === '#' && b === '#') return 0;
        if (a === '#') return 1;
        if (b === '#') return -1;
        return a.localeCompare(b, 'de-DE');
      });
      // Navigation letters complete A-Z even if empty to allow quick jump
      const fullLetters = Array.from({length:26}, (_,i)=>String.fromCharCode(65+i));
      const navLetters = fullLetters.filter(l => groups.has(l));
      const hasOthers = groups.has('#');
      return (
        <div style={{ display:'flex', flexDirection:'column', gap: 40 }}>
          {/* Sticky jump navigation */}
          <nav aria-label="Alphabetische Navigation"
            style={{ position:'sticky', top:0, zIndex:20, background:'linear-gradient(180deg, #131a2a 0%, rgba(19,26,42,0.85) 70%, rgba(19,26,42,0) 100%)', padding:'10px 0 14px', backdropFilter:'blur(6px)' }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, justifyContent:'center' }}>
              {navLetters.map(l => (
                <a key={l} href={`#letter-${l}`} style={{
                  textDecoration:'none',
                  fontSize:13,
                  fontWeight:600,
                  padding:'6px 10px',
                  lineHeight:'14px',
                  borderRadius:8,
                  background:'rgba(255,255,255,0.06)',
                  color:'#e5eaf3',
                  border:'1px solid rgba(255,255,255,0.15)',
                  transition:'background 120ms'
                }}
                  onMouseEnter={ev=>ev.currentTarget.style.background='rgba(50,205,137,0.35)'}
                  onMouseLeave={ev=>ev.currentTarget.style.background='rgba(255,255,255,0.06)'}
                >{l}</a>
              ))}
              {hasOthers && (
                <a href="#letter-others" style={{
                  textDecoration:'none', fontSize:13, fontWeight:600, padding:'6px 10px', lineHeight:'14px', borderRadius:8,
                  background:'rgba(255,255,255,0.06)', color:'#e5eaf3', border:'1px solid rgba(255,255,255,0.15)'
                }}>Andere</a>
              )}
            </div>
          </nav>
          {letters.map(letter => {
            const items = groups.get(letter);
            // Sort visible entries in group by displayName/username
            items.sort((a,b) => {
              const av = (a.entry.displayName || a.entry.username || '').localeCompare((b.entry.displayName || b.entry.username || ''), 'de-DE', { sensitivity:'base' });
              return av;
            });
            return (
              <div key={letter} id={`letter-${letter === '#' ? 'others' : letter}`}>
                <div style={{ fontSize: 24, fontWeight: 900, margin: '0 0 16px', letterSpacing: 1, display:'flex', alignItems:'center', gap:12, position:'sticky', top:54, zIndex:10,
                  background:'linear-gradient(90deg, rgba(19,26,42,0.95) 0%, rgba(19,26,42,0.85) 60%, rgba(19,26,42,0) 100%)', padding:'8px 0' }}>
                  <span style={{ padding:'6px 16px', borderRadius: 999, background:'rgba(50,205,137,0.18)', border:'1px solid rgba(50,205,137,0.55)', fontSize:36, lineHeight:'38px', fontWeight:800, letterSpacing:1 }}>{letter === '#' ? 'Andere' : letter}</span>
                  <span style={{ fontSize:13, fontWeight:500, color:'#9fb4d6' }}>{items.length} Mitglied{items.length===1?'':'er'}</span>
                </div>
                <CardContainer>
                  {items.map(({ entry, idx }) => {
                    if (entry.type === 'visible') {
                      return (
                        <Link
                          key={entry.id}
                          to={`/u/${entry.id}`}
                          style={visibleCardStyle}
                          onMouseEnter={(ev) => { ev.currentTarget.style.transform = 'translateY(-2px)'; ev.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.25)'; ev.currentTarget.style.borderColor = 'rgba(50,205,137,0.5)'; }}
                          onMouseLeave={(ev) => { ev.currentTarget.style.transform = 'none'; ev.currentTarget.style.boxShadow = 'none'; ev.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}
                          aria-label={(entry.displayName || entry.username) + ' – Profil öffnen'}
                        >
                          <img loading="lazy" src={resolveMediaUrl(entry.avatarUrl || '/avatars/default-avatar.png')} alt="Avatar" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', background: '#111', border: '2px solid #32CD89' }} />
                          <div style={{ display:'flex', flexDirection:'column' }}>
                            <div style={{ fontWeight: 800 }}>{entry.displayName || entry.username}</div>
                            <div style={{ fontSize: 12, color:'#9fb4d6' }}>{resolveDisplayRole(entry)}</div>
                          </div>
                        </Link>
                      );
                    }
                    // hidden placeholder
                    return (
                      <div
                        key={`ph-${idx}`}
                        title="Verstecktes Mitglied"
                        style={{ ...hiddenCardStyle, cursor: 'default' }}
                        aria-label="Verstecktes Mitglied"
                      >
                        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#131a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', border:'2px dashed #3a4b6a' }}>
                          <span role="img" aria-label="Hidden" style={{fontSize:22}}>👤</span>
                        </div>
                        <div>
                          <div style={{ fontWeight: 800 }}>Verstecktes Mitglied</div>
                          <div style={{ fontSize: 12, color: '#93a4bf' }}>Nicht sichtbar</div>
                        </div>
                      </div>
                    );
                  })}
                </CardContainer>
                <div style={{ marginTop: 16, textAlign:'right' }}>
                  <a href="#top" style={{ fontSize:12, textDecoration:'none', color:'#32CD89', opacity:0.85 }} onMouseEnter={ev=>ev.currentTarget.style.opacity='1'} onMouseLeave={ev=>ev.currentTarget.style.opacity='0.85'}>Zurück nach oben ↑</a>
                </div>
              </div>
            );
          })}
        </div>
      );
    })()
  );

  // simple skeletons while loading
  const Skeletons = () => (
    <CardContainer>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{
          width: 220,
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.03)',
          display:'flex', alignItems:'center', gap:12, padding:12
        }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(90deg, #233a69, #29437a, #233a69)', backgroundSize:'200% 100%', animation:'shimmer 1.2s infinite' }} />
          <div style={{ flex:1 }}>
            <div style={{ height:14, width:'70%', borderRadius:6, background:'linear-gradient(90deg, #233a69, #29437a, #233a69)', backgroundSize:'200% 100%', animation:'shimmer 1.2s infinite' }} />
            <div style={{ height:10, width:'40%', marginTop:8, borderRadius:6, background:'linear-gradient(90deg, #233a69, #29437a, #233a69)', backgroundSize:'200% 100%', animation:'shimmer 1.2s infinite' }} />
          </div>
        </div>
      ))}
      <style>{`@keyframes shimmer{0%{background-position:0% 0}100%{background-position:200% 0}}`}</style>
    </CardContainer>
  );

  return (
    <div className="home-page">
      {/* Hero/Intro */}
      <section id="top" style={{ width:'100vw', margin:0, padding:'28px 0 8px', background:'linear-gradient(180deg, rgba(19,26,42,0.6), rgba(19,26,42,0))' }}>
        <div style={{ maxWidth: 1000, margin:'0 auto', padding:'0 16px', textAlign:'center' }}>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: '0 0 6px' }}>Mitglieder</h1>
          <div style={{ opacity:0.85, color:'#c9d8f3' }}>
            Entdecke unsere Community. Sichtbarkeit richtet sich nach den Privatsphäre‑Einstellungen zur Mitgliedschaft.
          </div>
          {hiddenCount > 0 && (
            <div style={{ marginTop:8, fontSize:13, color:'#9fb4d6' }}>
              Hinweis: {hiddenCount} Mitglied{hiddenCount===1?'':'er'} erscheinen anonym.
              {!user && ' Tipp: Einloggen, um ggf. mehr zu sehen.'}
            </div>
          )}
        </div>
      </section>

      {/* List section */}
      <section style={{ width: '100vw', margin: 0, padding: '16px 0 32px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 16px' }}>
          {loading && <Skeletons />}
          {error && <div style={{ textAlign: 'center', marginTop: 12, color: '#f55' }}>{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: 20, color: '#aaa' }}>Keine Mitglieder sichtbar.</div>
          )}
          {!loading && !error && entries.length > 0 && cards}
        </div>
      </section>
    </div>
  );
}
